import WizardProgressBar from '@/components/shared/WizardProgressBar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import OrderFeedbackDialog from '@/components/wizard/OrderFeedbackDialog';
import OrderSummarySidebar from '@/components/wizard/OrderSummarySidebar';
import { clearWizardProgress, saveWizardProgress } from '@/lib/dataService';
import { paymentsApi } from '@/services/api';
import FileUploadService from '@/services/FileUploadService';
import { TAX_COMPUTATION_ACCEPT, TAX_COMPUTATION_ID, ordersTaxComputation, sendsTaxComputationLater, uploadsTaxComputationNow } from '@/lib/taxComputation';
import { getActiveRegionConfig } from '@/lib/regionConfig';
import { FINISHED_IXBRL_ACCEPT, FINISHED_IXBRL_CATEGORY, ordersOwnIxbrl } from '@/lib/ownIxbrl';
import { AlertTriangle, ArrowLeft, ArrowRight, Building2, CheckCircle2, ChevronLeft, ChevronRight, Clock, Download, FileText, Loader2, RefreshCw, Save, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

const Step4Upload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [wizardData, setWizardData] = useState(null);
  const [companyFiles, setCompanyFiles] = useState({});
  const [taxFiles, setTaxFiles] = useState({});   // the client's own tax computation per company, when ordered
  const needsTaxFile = uploadsTaxComputationNow(wizardData);
  const taxComputationByEmail = ordersTaxComputation(wizardData) && sendsTaxComputationLater(wizardData);
  const ownIxbrl = ordersOwnIxbrl(wizardData);                          // the one file per company is their finished iXBRL
  const accountsAccept = ownIxbrl ? FINISHED_IXBRL_ACCEPT : '.pdf,.docx,.xlsx';
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [versionUploadTarget, setVersionUploadTarget] = useState(null);
  const [changeDetection, setChangeDetection] = useState(null);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [showPartialDialog, setShowPartialDialog] = useState(false);
  const [uploadingCompanyId, setUploadingCompanyId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [submittedJobIds, setSubmittedJobIds] = useState([]);
  const fileInputRefs = useRef({});
  const bulkFileInputRef = useRef(null);

  // A file counts as stored once the server has returned its id. Anything else (waiting, uploading, failed)
  // is lost if the client leaves, so they are warned.
  const hasUnsentFiles = [...Object.values(companyFiles), ...Object.values(taxFiles)].some(f => f.file && !f.file_id);
  useEffect(() => {
    if (!hasUnsentFiles) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [hasUnsentFiles]);

  useEffect(() => {
    const data = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    if (!data.paymentType || !data.createdJobs?.length) {
      toast.error('Session expired. Please start again.');
      navigate('/wizard/start');
      return;
    }
    setWizardData(data);

    const stripeSessionId = searchParams.get('stripe_session_id') || searchParams.get('pay_ref');

    if (stripeSessionId && data.paymentType === 'pay_now') {
      setVerifyingPayment(true);
      paymentsApi.verifyReturn(searchParams)
        .then(result => {
          if (result.paid) {
            setPaymentVerified(true);
            toast.success('Payment confirmed! Now upload your documents.');
            const updated = { ...data, stripeSessionId };
            sessionStorage.setItem('ixbrl_wizard', JSON.stringify(updated));
            setWizardData(updated);
          } else if (result.processing) {
            // Checkout finished but the payment is still settling: do NOT send the
            // client back to pay again.
            toast.info('Your payment is being processed. Please do not pay again - check your Jobs page in a minute.');
            navigate('/jobs');
          } else {
            toast.error('Payment not confirmed. You have not been charged - you can try again from the review step.');
            navigate('/wizard/review');
          }
        })
        .catch(() => {
          // Could not reach the server: the payment may well have gone through.
          toast.error('We could not confirm your payment. If you were charged, please do not pay again - check your Jobs page or contact support.');
          navigate('/jobs');
        })
        .finally(() => setVerifyingPayment(false));
    } else if (data.paymentType === 'invoice_later' || data.paymentType === 'credit') {
      // Credit approval happens after submission via Provider Portal
      // (approve_credit), not before -- gating the wizard's own submit
      // button on payment verification for credit customers would block
      // every credit order at this step forever.
      setPaymentVerified(true);
    } else if (data.stripeSessionId) {
      // Returning to this page after payment was already verified
      setPaymentVerified(true);
    }
  }, [navigate, searchParams]);

  const getEstimatedDelivery = () => {
    const now = new Date();
    const hours = wizardData?.config?.deliveryTime === 'express' ? 8 : 24;
    now.setHours(now.getHours() + hours);
    return now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const createdJobFor = (companyId) => (wizardData?.createdJobs || []).find(j => j.company_id === companyId);

  const sendToJob = (createdJob, file, fileCategory, onProgress = null) => FileUploadService.uploadFile(
    file,
    createdJob.company_id,
    createdJob.company_name,
    onProgress,
    { jobId: createdJob.id, jobReference: createdJob.job_id, ...(fileCategory ? { fileCategory } : {}) },
  );

  const handleFileSelect = (e, companyId) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) handleFileForCompany(companyId, selectedFile);
    e.target.value = '';
  };

  const handleFileForCompany = (companyId, file) => {
    const existingFile = companyFiles[companyId];

    if (existingFile && existingFile.status === 'uploaded') {
      setVersionUploadTarget({ companyId, newFile: file });
      setShowVersionDialog(true);
      return;
    }

    uploadFile(companyId, file);
  };

  const uploadFile = async (companyId, file, isVersionUpdate = false) => {
    // Validate file
    const validation = FileUploadService.validateFile(file, ownIxbrl ? FINISHED_IXBRL_CATEGORY : undefined);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    if (validation.warning) {
      toast.warning(validation.warning);
    }

    // Find company name for upload
    const company = wizardData?.selectedCompanies?.find(c => c.id === companyId);
    if (!company) {
      toast.error('Company not found');
      return;
    }

    const staged = { file, name: file.name, size: file.size, status: 'uploaded' };
    const createdJob = createdJobFor(companyId);

    // Before payment is confirmed (or without a job) the file can only wait in the browser; the submit
    // button sends it. Otherwise the job already exists, so it is sent now: a file that only waits in
    // the browser is lost when the page is closed or the session ends.
    if (!paymentVerified || !createdJob) {
      setCompanyFiles(prev => ({ ...prev, [companyId]: staged }));
      toast.success(`${file.name} added${isVersionUpdate ? ' (new version)' : ''}. It is sent when you submit.`);
      return;
    }

    const accountsCategory = ownIxbrl ? FINISHED_IXBRL_CATEGORY : undefined;
    const patchWhileCurrent = (changes) => setCompanyFiles(prev => (
      prev[companyId]?.file === file ? { ...prev, [companyId]: { ...prev[companyId], ...changes } } : prev
    ));

    setCompanyFiles(prev => ({ ...prev, [companyId]: { ...staged, status: 'uploading', progress: 0 } }));
    try {
      const result = await sendToJob(createdJob, file, accountsCategory, (progress) => patchWhileCurrent({ progress }));
      patchWhileCurrent({
        status: 'uploaded',
        progress: 100,
        error: null,
        file_id: result.file_id,
        storage_path: result.storage_path,
        checksum_sha256: result.checksum_sha256,
        backend_status: result.status,
      });
      toast.success(`${file.name} uploaded${isVersionUpdate ? ' (new version)' : ''}`);
    } catch (error) {
      patchWhileCurrent({ status: 'failed', error: error.message || 'Upload failed' });
      toast.error(`${file.name} did not upload: ${error.message || 'please try again'}`);
    }
  };

  const handleTaxFileSelect = async (e, companyId) => {
    const selected = e.target.files[0];
    e.target.value = '';
    if (!selected) return;
    const validation = FileUploadService.validateFile(selected, ownIxbrl ? FINISHED_IXBRL_CATEGORY : undefined);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }
    if (validation.warning) toast.warning(validation.warning);

    const staged = { file: selected, name: selected.name, size: selected.size, status: 'staged' };
    const createdJob = createdJobFor(companyId);
    if (!paymentVerified || !createdJob) {
      setTaxFiles(prev => ({ ...prev, [companyId]: staged }));
      toast.success(`${selected.name} added as the tax computation. It is sent when you submit.`);
      return;
    }

    const patchWhileCurrent = (changes) => setTaxFiles(prev => (
      prev[companyId]?.file === selected ? { ...prev, [companyId]: { ...prev[companyId], ...changes } } : prev
    ));
    setTaxFiles(prev => ({ ...prev, [companyId]: { ...staged, status: 'uploading' } }));
    try {
      const result = await sendToJob(createdJob, selected, 'tax_computation');
      patchWhileCurrent({ status: 'uploaded', error: null, file_id: result.file_id });
      toast.success(`${selected.name} uploaded as the tax computation`);
    } catch (error) {
      patchWhileCurrent({ status: 'failed', error: error.message || 'Upload failed' });
      toast.error(`${selected.name} did not upload: ${error.message || 'please try again'}`);
    }
  };

  const removeTaxFile = (companyId) => {
    setTaxFiles(prev => {
      const updated = { ...prev };
      delete updated[companyId];
      return updated;
    });
  };

  const handleVersionUpdate = (updateType) => {
    if (!versionUploadTarget) return;

    const { companyId, newFile } = versionUploadTarget;

    if (updateType === 'smart') {
      setChangeDetection({
        companyId,
        unchanged: 95,
        modified: 3,
        added: 2,
        estimatedTime: '2 mins'
      });
    } else {
      uploadFile(companyId, newFile, true);
    }

    setShowVersionDialog(false);
  };

  const confirmSmartUpdate = () => {
    if (!changeDetection || !versionUploadTarget) return;
    const { companyId } = changeDetection;
    uploadFile(companyId, versionUploadTarget.newFile, true);
    setChangeDetection(null);
    setVersionUploadTarget(null);
    toast.success('Smart update initiated - only modified pages will be re-tagged');
  };

  const removeFile = (companyId) => {
    setCompanyFiles(prev => {
      const updated = { ...prev };
      delete updated[companyId];
      return updated;
    });
  };

  const handleBulkUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const companiesWithoutFiles = selectedForBulk.filter(id => !companyFiles[id]);

    files.forEach((file, idx) => {
      if (idx < companiesWithoutFiles.length) {
        uploadFile(companiesWithoutFiles[idx], file);
      }
    });

    setSelectedForBulk([]);
    e.target.value = '';
  };

  const handleSelectForBulk = (companyId, checked) => {
    if (checked) {
      setSelectedForBulk([...selectedForBulk, companyId]);
    } else {
      setSelectedForBulk(selectedForBulk.filter(id => id !== companyId));
    }
  };

  const handleSaveProgress = async () => {
    const currentData = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    currentData.uploadedFiles = Object.keys(companyFiles).map(id => ({
      companyId: id,
      fileName: companyFiles[id].name
    }));
    currentData.savedAt = new Date().toISOString();
    await saveWizardProgress(currentData);
    toast.success('Progress saved!');
  };

  const handleComplete = async () => {
    const allUploaded = wizardData.selectedCompanies.every(
      c => companyFiles[c.id]?.status === 'uploaded' && (!needsTaxFile || taxFiles[c.id])
    );

    if (!allUploaded) {
      setShowPartialDialog(true);
      return;
    }

    try {
      await submitJobs(wizardData.selectedCompanies);
    } catch (err) {
      toast.error(err.message || 'Failed to submit jobs. Please try again.');
    }
  };

  const submitJobs = async (companiesToSubmit) => {
    // Jobs (and their invoices) are already created in Step 3 -- this only
    // uploads the staged files against those existing jobs.
    const createdJobs = wizardData.createdJobs || [];
    const jobsToUpload = createdJobs.filter(
      j => companiesToSubmit.some(c => c.id === j.company_id) && companyFiles[j.company_id]?.status === 'uploaded'
    );

    for (const createdJob of jobsToUpload) {
      const staged = companyFiles[createdJob.company_id];
      if (!staged?.file) continue;

      // Files chosen after payment were already sent when chosen; only the ones still waiting go up here.
      if (!staged.file_id) {
        setUploadingCompanyId(createdJob.company_id);
        setUploadProgress(0);

        const uploadResult = await sendToJob(
          createdJob,
          staged.file,
          ownIxbrl ? FINISHED_IXBRL_CATEGORY : undefined,
          (progress) => setUploadProgress(progress),
        );

        setCompanyFiles(prev => ({
          ...prev,
          [createdJob.company_id]: {
            ...prev[createdJob.company_id],
            file_id: uploadResult.file_id,
            storage_path: uploadResult.storage_path,
            checksum_sha256: uploadResult.checksum_sha256,
            backend_status: uploadResult.status
          }
        }));
      }

      // The client's own tax computation goes up as a separate file of the same job.
      const stagedTax = needsTaxFile ? taxFiles[createdJob.company_id] : null;
      if (stagedTax?.file && !stagedTax.file_id) {
        const taxResult = await sendToJob(createdJob, stagedTax.file, 'tax_computation');
        setTaxFiles(prev => ({
          ...prev,
          [createdJob.company_id]: { ...prev[createdJob.company_id], status: 'uploaded', file_id: taxResult.file_id },
        }));
      }
    }

    setUploadingCompanyId(null);
    setUploadProgress(0);

    const companiesWithoutFiles = wizardData.selectedCompanies.filter(
      c => !companyFiles[c.id] || companyFiles[c.id].status !== 'uploaded' || (needsTaxFile && !taxFiles[c.id])
    );

    // Jobs already exist for every company (created in Step 3) -- companies
    // without a file yet keep their job in a file-pending state, rather
    // than having no job row at all until a file is attached.
    if (companiesWithoutFiles.length > 0) {
      const draftData = {
        ...wizardData,
        selectedCompanies: companiesWithoutFiles,
        step: 4,
        savedAt: new Date().toISOString(),
        partialSubmitNote: `${jobsToUpload.length} file(s) uploaded. ${companiesWithoutFiles.length} job(s) still awaiting a file.`,
      };
      await saveWizardProgress(draftData);
      toast.success(`${jobsToUpload.length} file(s) uploaded. ${companiesWithoutFiles.length} companies saved as draft for later.`);
      sessionStorage.removeItem('ixbrl_wizard');
    } else {
      await clearWizardProgress();
      toast.success(`${createdJobs.length} job(s) submitted successfully!`);

      sessionStorage.removeItem('ixbrl_wizard');
    }

    setSubmittedJobIds(createdJobs.map(j => j.job_id).filter(Boolean));
    setShowFeedbackDialog(true);
  };

  const handleFeedbackDialogClose = () => {
    setShowFeedbackDialog(false);
    navigate('/jobs');
  };

  if (!wizardData) return null;

  if (verifyingPayment) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Verifying payment...</p>
      </div>
    );
  }

  const taxUploadedCount = wizardData.selectedCompanies.filter(c => taxFiles[c.id]).length;
  const uploadedCount = Object.values(companyFiles).filter(f => f.status === 'uploaded').length; // stored or waiting for submit
  const storedCount = Object.values(companyFiles).filter(f => f.file_id).length;
  const anyUploading = [...Object.values(companyFiles), ...Object.values(taxFiles)].some(f => f.status === 'uploading');
  const totalCompanies = wizardData.selectedCompanies.length;
  const companiesWithoutFiles = wizardData.selectedCompanies.filter(c => !companyFiles[c.id]);

  // Pagination
  const totalPages = Math.ceil(totalCompanies / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCompanies = wizardData.selectedCompanies.slice(startIndex, endIndex);

  const getServicesList = () => {
    const names = {
      hmrc_filing: 'HMRC CT600 Filing',
      [TAX_COMPUTATION_ID]: 'Tax Computation',
      companies_house_filing: 'CH Filing',
      // legacy keys
      financialStatements: 'iXBRL Financials',
      taxComputation: 'iXBRL Tax',
      ct600: 'CT600',
      companiesHouse: 'CH Filing',
    };
    const services = Array.isArray(wizardData?.optionalServices)
      ? wizardData.optionalServices
      : Object.keys(wizardData?.solutions || {}).filter(k => wizardData.solutions[k]);
    return services.length > 0 ? services.map(k => names[k] || k).join(', ') : 'iXBRL Tagging';
  };

  return (
    <div data-testid="step4-upload">
      <div className="page-header">
        <h1 className="page-title">Create New Job</h1>
        <p className="page-subtitle">Step 4 of 4: Upload documents for conversion</p>
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          {/* Upload Progress Summary */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Upload Progress</span>
              <span className="text-sm text-muted-foreground">{storedCount} of {totalCompanies} files uploaded</span>
            </div>
            <Progress value={(storedCount / totalCompanies) * 100} className="h-2" />
            {hasUnsentFiles && (
              <p className="text-xs text-amber-700 mt-2" data-testid="unsent-files-note">
                {paymentVerified
                  ? 'Some files have not finished uploading. Stay on this page until every file shows Uploaded.'
                  : 'Your files are sent when payment is confirmed and you submit. Stay on this page until then.'}
              </p>
            )}
          </div>

          {/* Bulk Upload Actions */}
          {companiesWithoutFiles.length > 1 && (
            <div className="bg-muted/50 border border-border rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedForBulk.length === companiesWithoutFiles.length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedForBulk(companiesWithoutFiles.map(c => c.id));
                      } else {
                        setSelectedForBulk([]);
                      }
                    }}
                  />
                  <span className="text-sm">
                    {selectedForBulk.length > 0
                      ? `${selectedForBulk.length} selected for bulk upload`
                      : 'Select for bulk upload'}
                  </span>
                </div>
                {selectedForBulk.length > 0 && (
                  <div>
                    <input
                      ref={bulkFileInputRef}
                      type="file"
                      accept={accountsAccept}
                      multiple
                      onChange={handleBulkUpload}
                      className="hidden"
                    />
                    <Button size="sm" onClick={() => bulkFileInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {selectedForBulk.length} Files
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Companies Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-sm">Upload Documents ({totalCompanies} companies)</h3>
              <Button variant="ghost" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download Sample
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium w-10">#</th>
                    <th className="text-left py-2 px-3 font-medium">Company</th>
                    <th className="text-left py-2 px-3 font-medium">Account Type</th>
                    <th className="text-left py-2 px-3 font-medium">Services</th>
                    <th className="text-left py-2 px-3 font-medium">Est. Delivery</th>
                    <th className="text-left py-2 px-3 font-medium">File</th>
                    <th className="text-left py-2 px-3 font-medium">Status</th>
                    <th className="text-center py-2 px-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCompanies.map((company, idx) => {
                    const file = companyFiles[company.id];
                    const isSelected = selectedForBulk.includes(company.id);
                    const rowNum = startIndex + idx + 1;

                    return (
                      <tr key={company.id} className={`border-t border-border ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/30'}`}>
                        <td className="py-2 px-3">
                          {!file && companiesWithoutFiles.length > 1 ? (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleSelectForBulk(company.id, checked)}
                            />
                          ) : (
                            <span className="text-muted-foreground">{rowNum}</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate max-w-[150px]" title={company.name}>{company.name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{company.accountType}</span>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-xs text-muted-foreground">{getServicesList()}</span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-1 text-xs">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span>{getEstimatedDelivery()}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          {file ? (
                            <div className="flex items-center gap-1">
                              <FileText className="w-3 h-3 text-green-600" />
                              <span className="text-xs truncate max-w-[100px]" title={file.name}>{file.name}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {file?.status === 'uploading' ? (
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground">
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                Uploading
                              </span>
                              <span className="text-xs text-muted-foreground">{file.progress || 0}%</span>
                            </div>
                          ) : file?.status === 'failed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700" title={file.error}>
                              <AlertTriangle className="w-3 h-3" />
                              Not uploaded
                            </span>
                          ) : file && !file.file_id ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                              Not sent yet
                            </span>
                          ) : file ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3" />
                              Uploaded
                            </span>
                          ) : uploadingCompanyId === company.id ? (
                            <div className="flex items-center gap-2 w-24">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground">
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                Uploading
                              </span>
                              <span className="text-xs text-muted-foreground">{uploadProgress}%</span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            ref={el => fileInputRefs.current[company.id] = el}
                            type="file"
                            accept={accountsAccept}
                            onChange={(e) => handleFileSelect(e, company.id)}
                            className="hidden"
                            disabled={uploadingCompanyId === company.id || file?.status === 'uploading'}
                          />
                          {uploadingCompanyId === company.id || file?.status === 'uploading' ? (
                            <div className="flex items-center justify-center">
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            </div>
                          ) : file ? (
                            <div className="flex items-center justify-center gap-1">
                              {file.status === 'failed' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => uploadFile(company.id, file.file)}
                                  data-testid={`retry-upload-${company.id}`}
                                >
                                  Retry
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => fileInputRefs.current[company.id]?.click()}
                              >
                                <RefreshCw className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => removeFile(company.id)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => fileInputRefs.current[company.id]?.click()}
                            >
                              Browse
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Tax computation files: one per company, uploaded with the accounts */}
            {needsTaxFile && (
              <div className="border-t border-border p-3" data-testid="tax-computation-uploads">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Tax computation files</h3>
                  <span className="text-xs text-muted-foreground">{taxUploadedCount} of {totalCompanies} added</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Add your own tax computation for each company (PDF, Word or Excel). We convert it to iXBRL. No charge.
                </p>
                <div className="space-y-2">
                  {paginatedCompanies.map((company) => {
                    const taxFile = taxFiles[company.id];
                    return (
                      <div key={company.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`tax-file-row-${company.id}`}>
                        <span className="truncate max-w-[220px]" title={company.name}>{company.name}</span>
                        <div className="flex items-center gap-2">
                          {taxFile ? (
                            <>
                              <FileText className="w-3 h-3 text-green-600" />
                              <span className="text-xs truncate max-w-[160px]" title={taxFile.name}>{taxFile.name}</span>
                              <span className={`text-xs ${taxFile.status === 'failed' ? 'text-red-700' : taxFile.file_id ? 'text-green-700' : 'text-amber-700'}`}>
                                {taxFile.status === 'uploading' ? 'Uploading…' : taxFile.status === 'failed' ? 'Not uploaded' : taxFile.file_id ? 'Uploaded' : 'Not sent yet'}
                              </span>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeTaxFile(company.id)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-amber-700">Needed</span>
                          )}
                          <input
                            id={`tax-file-${company.id}`}
                            type="file"
                            accept={TAX_COMPUTATION_ACCEPT}
                            onChange={(e) => handleTaxFileSelect(e, company.id)}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => document.getElementById(`tax-file-${company.id}`)?.click()}
                          >
                            {taxFile ? 'Replace' : 'Browse'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tax computation sent separately: nothing to upload here, tell the client where to send it */}
            {taxComputationByEmail && (
              <div className="border-t border-border p-3 text-xs text-muted-foreground" data-testid="tax-computation-by-email">
                <span className="font-semibold text-foreground">Tax computation: </span>
                email it to {getActiveRegionConfig().supportEmail} quoting your job reference. It is converted to iXBRL free of charge,
                and HMRC filing cannot go ahead until we have it.
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-3 border-t border-border flex items-center justify-between bg-muted/30">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, totalCompanies)} of {totalCompanies}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Select value={currentPage.toString()} onValueChange={(v) => setCurrentPage(parseInt(v))}>
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>Page {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Dialogs */}
          <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Replace Existing File?
                </DialogTitle>
                <DialogDescription>
                  A file has already been uploaded. How would you like to handle the new version?
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4">
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => handleVersionUpdate('smart')}
                >
                  <div className="text-left">
                    <p className="font-medium">Smart Update (Recommended)</p>
                    <p className="text-sm text-muted-foreground">Only re-tag changed pages</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => handleVersionUpdate('full')}
                >
                  <div className="text-left">
                    <p className="font-medium">Full Re-tag</p>
                    <p className="text-sm text-muted-foreground">Re-process the entire document</p>
                  </div>
                </Button>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowVersionDialog(false)}>Cancel</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={!!changeDetection} onOpenChange={() => setChangeDetection(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Detection Results</DialogTitle>
              </DialogHeader>
              {changeDetection && (
                <div className="py-4">
                  <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Pages unchanged</span>
                      <span className="font-medium text-green-600">{changeDetection.unchanged}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pages modified</span>
                      <span className="font-medium text-amber-600">{changeDetection.modified}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pages added</span>
                      <span className="font-medium text-foreground">{changeDetection.added}</span>
                    </div>
                    <div className="border-t border-border pt-2 mt-2 flex justify-between">
                      <span>Estimated time</span>
                      <span className="font-medium">{changeDetection.estimatedTime}</span>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setChangeDetection(null); setVersionUploadTarget(null); }}>Cancel</Button>
                <Button onClick={confirmSmartUpdate}>Proceed</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Partial submission dialog */}
          <Dialog open={showPartialDialog} onOpenChange={setShowPartialDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Submit with Missing Files?
                </DialogTitle>
                <DialogDescription>
                  {uploadedCount} of {totalCompanies} files are uploaded. You can submit the ready ones now and upload the rest later.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">Ready to submit</span>
                  <span className="font-medium">{uploadedCount} companies</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-700">Saved as draft for later</span>
                  <span className="font-medium">{totalCompanies - uploadedCount} companies</span>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPartialDialog(false)}>
                  Continue Uploading
                </Button>
                <Button onClick={() => { setShowPartialDialog(false); submitJobs(wizardData.selectedCompanies); }} disabled={uploadedCount === 0}>
                  Submit {uploadedCount} Now
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <OrderFeedbackDialog
            open={showFeedbackDialog}
            onClose={handleFeedbackDialogClose}
            jobIds={submittedJobIds}
          />

          <div className="flex justify-between mt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (hasUnsentFiles && !window.confirm('Some files have not been uploaded yet and will be lost if you leave. Leave anyway?')) return;
                  navigate('/wizard/review');
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button variant="ghost" onClick={handleSaveProgress}>
                <Save className="w-4 h-4 mr-2" />
                Save Progress
              </Button>
            </div>
            <Button
              onClick={handleComplete}
              disabled={uploadedCount === 0 || !paymentVerified || anyUploading}
              data-testid="complete-btn"
            >
              {wizardData.config.serviceType === 'diy' ? 'Start Conversion' : 'Submit to Expert'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Order Summary Sidebar - Top Aligned */}
        <div className="w-72 flex-shrink-0">
          <WizardProgressBar currentStep={4} />
          <OrderSummarySidebar wizardData={wizardData} currentStep={4} />
        </div>
      </div>
    </div>
  );
};

export default Step4Upload;
